import React, {
    useEffect,
    ReactElement,
    useRef,
    MouseEventHandler
} from 'react';
import styled, { css } from 'styled-components';
import { vars } from '../../theme';
import { Plus16 } from '@honeybook/icons';
import { Tabs } from './HBTabs.styles';
import { MustIncludeOneOfBasicTestIdProps } from '../common/internal/test-id.types';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import { ITooltipProps } from '../HBTooltip/HBTooltip';
import HBIconButton from '../HBIconButton/HBIconButton';
import { TMenuItem } from '../HBMenu/HBMenu.types';
import TabItem from './TabItem';
import GradientScrollContainer from './GradientScrollContainer';
import type { TButtonSize } from '../HBButton/HBButton.types';

const addTabBottomPositionBySize: Record<HBTabsProps['size'], `${string}px`> = {
    sm: '-6px',
    md: '-4px',
    lg: '-2px'
};

export interface TabType {
    title?: string;
    icon?: ReactElement;
    /** Prevents interaction and applies disabled styling to the tab. Disabled tabs are skipped during auto-selection. */
    isDisabled?: boolean;
    id: string;
    /** Dropdown menu items shown when the tab has an attached menu; renders a menu trigger on the tab. */
    menuItems?: TMenuItem[];
    testId?: string;
    /** Route path for router integration; renders the tab as a link via HBUIProvider's LinkComponent. */
    to?: string;
    /** Element rendered after the tab title (e.g., a badge or count indicator). */
    suffix?: ReactElement;
}

export type TTabSize = Extract<TButtonSize, 'sm' | 'md' | 'lg'>;

interface RenderTabProps extends TabType {
    size?: TTabSize;
    isActive: boolean;
    onClick: () => void;
}

export interface HBTabsProps {
    /** CSS class applied to the root element. */
    className?: string;
    /** Array of tab items to render. Each item requires an `id` and either a `title`, an `icon`, or both. Omit `title` on all items to enable icon-only layout. */
    tabs: TabType[];
    /** The id of the currently active tab; if omitted, the first non-disabled tab is auto-selected on mount. */
    selectedTab?: string;
    /** Tab size — controls text scale and spacing: 'sm', 'md' (default), or 'lg'. */
    size?: TTabSize;
    /** When true, tabs expand equally to fill the full container width. */
    stretchToFill?: boolean;
    /** Called with the tab id when a tab is clicked; also fires on mount with the auto-selected tab id when no selectedTab is provided. */
    onClick?(val: string): void;
    /** Maximum pixel width for each tab; tabs exceeding this width will truncate their content. */
    maxTabWidth?: number;
    /** Configuration for an add-tab button rendered after the tab list; renderWrapper allows wrapping with custom components (e.g., a tooltip or permission gate). */
    addTabProps?: {
        isDisabled?: boolean;
        onClick: () => void;
        tooltip?: string;
        tooltipProps?: Partial<ITooltipProps>;
        renderWrapper?: (addTabButton: ReactElement) => ReactElement;
    };
    /** When true, removes the bottom box-shadow border from the tabs container. */
    noShadow?: boolean;
    /** Custom CSS color for the active tab's bottom highlight indicator. */
    selectedHighlightColor?: string;
    /** Custom render function for each tab, receiving tab data plus isActive and onClick; overrides default tab rendering. */
    renderTab?: (tab: RenderTabProps) => ReactElement;
}

const AddTabButton = ({
    addTabProps,
    size
}: Pick<HBTabsProps, 'size' | 'addTabProps'>) => {
    const onAddTabClick: MouseEventHandler<HTMLElement> = ev => {
        ev.currentTarget.blur();
        addTabProps.onClick();
    };

    return (
        <HBIconButton
            buttonTheme="neutral-invisible"
            disabled={addTabProps.isDisabled}
            icon={Plus16}
            label="add tab"
            onClick={onAddTabClick}
            size="xs"
            testIdName="add-tab"
            tooltip={addTabProps.tooltip}
            tooltipProps={{
                targetContainerStyle: {
                    alignSelf: 'start',
                    transform: `translateY(${addTabBottomPositionBySize[size]})`
                },
                ...addTabProps.tooltipProps
            }}
        />
    );
};

const HBTabs = ({
    className,
    testIdName,
    testId,
    tabs,
    selectedTab,
    size = 'md',
    stretchToFill = false,
    onClick,
    maxTabWidth,
    addTabProps,
    noShadow,
    selectedHighlightColor,
    renderTab
}: HBTabsProps & MustIncludeOneOfBasicTestIdProps) => {
    const scrollRef = useRef(null);

    const isIconOnly = tabs.every(tab => !tab.title);
    const selected = selectedTab || tabs.find(tab => !tab.isDisabled)?.id;

    useEffect(() => {
        if (!selectedTab && selected && onClick) {
            onClick(selected);
        }
    }, [onClick, selected, selectedTab]);

    const tabsContainerTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName,
        suffix: 'tabs-container',
        componentName: 'HBTabs'
    });

    return (
        <FlexContainer className={className} noShadow={noShadow}>
            <GradientScrollContainer
                itemCount={tabs.length}
                scrollRef={scrollRef}
                stretchToFill={stretchToFill}
            >
                <Tabs
                    ref={scrollRef}
                    role="tablist"
                    data-testid={tabsContainerTestId}
                >
                    {tabs.map(tab => (
                        <TabItem
                            key={tab.id}
                            hasMenu={!!tab.menuItems?.length}
                            isActive={selected === tab.id}
                            isIconOnly={isIconOnly}
                            maxTabWidth={maxTabWidth}
                            onClick={onClick}
                            renderTab={renderTab}
                            scrollRef={scrollRef}
                            selectedHighlightColor={selectedHighlightColor}
                            size={size}
                            stretchToFill={stretchToFill}
                            tab={tab}
                        />
                    ))}
                </Tabs>
            </GradientScrollContainer>

            {addTabProps && (
                <>
                    {addTabProps.renderWrapper ? (
                        addTabProps.renderWrapper(
                            <AddTabButton
                                addTabProps={addTabProps}
                                size={size}
                            />
                        )
                    ) : (
                        <AddTabButton addTabProps={addTabProps} size={size} />
                    )}
                </>
            )}
        </FlexContainer>
    );
};

export default HBTabs;

// -------------------------------- Styles -------------------------------- //
const FlexContainer = styled.div<{ noShadow: boolean }>`
    display: flex;
    gap: 4px;
    ${({ noShadow }) =>
        !noShadow &&
        css`
            box-shadow: inset 0 -1px 0 ${vars.colors.stroke.secondary};
        `}
`;
